// ManageDrawer — tabbed drawer showing accrual/commitment/planned-spend lists.
// Hosts InputRow (per-record actions: Edit, Retire, Cancel) and EditForm (inline).

import { useState, useCallback } from "react";
import { Plus, X, AlertCircle, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useChangeForecastInputStatus,
  useUpdateForecastInput,
  getListForecastInputsQueryKey,
} from "@workspace/api-client-react";
import type {
  ForecastInputRecord,
  ForecastInputList,
  ForecastInputOverlap,
  ForecastOutlook,
} from "@workspace/api-client-react";
import { OverlapWarning } from "./schedule-builder";
import { StaleBanner } from "./stale-banner";
import type { ForecastContext, InputType, ManageTab } from "./types";
import { TAB_LABELS } from "./types";
import { formatPeriod, formatAmount, statusLabel, statusTone } from "./helpers";

// ── InputRow ─────────────────────────────────────────────────────────────────

interface InputRowProps {
  record: ForecastInputRecord;
  onStatusChange: (id: string, status: string, note?: string, actualsRef?: string) => void;
  onEdit: (record: ForecastInputRecord) => void;
  isAuthoritative: boolean;
}

function InputRow({ record, onStatusChange, onEdit, isAuthoritative }: InputRowProps) {
  const [retiring, setRetiring] = useState(false);
  const [retireRef, setRetireRef] = useState("");
  const [retireNote, setRetireNote] = useState("");
  const canRetire = record.inputType === "accrual" && record.status === "open";
  const canCancel = ["open", "active", "approved", "draft"].includes(record.status);

  return (
    <tr data-testid={`fi-input-row-${record.inputId}`}>
      <td>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <b style={{ fontSize: 11 }}>{record.description}</b>
          {record.vendor && <span className="mini">{record.vendor}</span>}
        </div>
      </td>
      <td className="mini">
        {record.schedules.length > 0
          ? record.schedules.map(s => formatPeriod(s.period, s.fiscalYear)).join(", ")
          : "—"}
      </td>
      <td>{formatAmount(record.amount)}</td>
      <td className="mini">{record.glAccount || "—"}</td>
      <td>
        <span
          className={`source-status ${statusTone(record.status)}`}
          style={{ fontSize: 9, padding: "3px 6px" }}
        >
          {statusLabel(record.status)}
        </span>
      </td>
      <td>
        {!isAuthoritative && (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button
              className="tl-fy-action"
              onClick={() => onEdit(record)}
              aria-label={`Edit ${record.description}`}
              data-testid={`fi-edit-${record.inputId}`}
            >
              Edit
            </button>
            {canRetire && !retiring && (
              <button
                className="tl-fy-action"
                onClick={() => setRetiring(true)}
                aria-label={`Retire ${record.description}`}
                data-testid={`fi-retire-${record.inputId}`}
              >
                Retire
              </button>
            )}
            {canCancel && (
              <button
                className="tl-fy-action"
                style={{ color: "#8d4b39" }}
                onClick={() => onStatusChange(record.inputId, "cancelled")}
                aria-label={`Cancel ${record.description}`}
                data-testid={`fi-cancel-${record.inputId}`}
              >
                Cancel
              </button>
            )}
          </div>
        )}
        {retiring && (
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="text"
              value={retireRef}
              onChange={e => setRetireRef(e.target.value)}
              placeholder="Linked actual ref (optional)"
              aria-label="Linked actuals reference for retirement"
              style={{ fontSize: 10, padding: "4px 6px", border: "1px solid #d5deda", borderRadius: 5, width: 160 }}
              data-testid={`fi-retire-ref-${record.inputId}`}
            />
            <input
              type="text"
              value={retireNote}
              onChange={e => setRetireNote(e.target.value)}
              placeholder="Retirement note (optional)"
              aria-label="Retirement note"
              style={{ fontSize: 10, padding: "4px 6px", border: "1px solid #d5deda", borderRadius: 5, width: 160 }}
              data-testid={`fi-retire-note-${record.inputId}`}
            />
            <button
              className="tl-fy-action primary"
              onClick={() => {
                onStatusChange(record.inputId, "retired", retireNote || undefined, retireRef || undefined);
                setRetiring(false);
              }}
              disabled={!retireRef.trim()}
              aria-label={`Confirm retirement of ${record.description}`}
              data-testid={`fi-retire-confirm-${record.inputId}`}
            >
              Confirm Retire
            </button>
            <button
              className="tl-fy-action"
              onClick={() => {
                setRetiring(false);
                setRetireRef("");
                setRetireNote("");
              }}
              aria-label="Cancel retirement"
              data-testid={`fi-retire-cancel-${record.inputId}`}
            >
              Back
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

// ── EditForm ──────────────────────────────────────────────────────────────────

interface EditFormProps {
  record: ForecastInputRecord;
  onSaved: () => void;
  onCancel: () => void;
}

function EditForm({ record, onSaved, onCancel }: EditFormProps) {
  const [description, setDescription] = useState(record.description);
  const [glAccount, setGlAccount] = useState(record.glAccount);
  const [vendor, setVendor] = useState(record.vendor ?? "");
  const [owner, setOwner] = useState(record.owner ?? "");
  const [note, setNote] = useState(record.note ?? "");
  const [amount, setAmount] = useState(String(record.amount));
  const [sourceRef, setSourceRef] = useState(record.sourceReference);
  const [overlaps, setOverlaps] = useState<ForecastInputRecord[]>([]);

  const updateMutation = useUpdateForecastInput();

  const handleSave = useCallback((saveAnyway = false) => {
    updateMutation.mutate({
      inputId: record.inputId,
      data: {
        actor: "ER",
        description,
        glAccount,
        vendor: vendor || undefined,
        owner: owner || undefined,
        note: note || undefined,
        amount: Number(amount),
        sourceReference: sourceRef || undefined,
        saveAnyway,
      },
    }, {
      onSuccess: () => { setOverlaps([]); onSaved(); },
      onError: (err: unknown) => {
        const overlap = err as ForecastInputOverlap;
        if (overlap?.code === "POSSIBLE_OVERLAP") setOverlaps(overlap.overlaps ?? []);
      },
    });
  }, [record.inputId, description, glAccount, vendor, owner, note, amount, sourceRef, updateMutation, onSaved]);

  if (overlaps.length > 0) {
    return (
      <OverlapWarning
        overlaps={overlaps}
        onCancel={() => setOverlaps([])}
        isPending={updateMutation.isPending}
        onSaveAnyway={() => handleSave(true)}
      />
    );
  }

  return (
    <div className="fi-form" data-testid={`fi-edit-form-${record.inputId}`}>
      <div className="fi-edit-header">
        <span className="eyebrow">EDIT {record.inputType.replace("_", " ").toUpperCase()}</span>
        <span className="fi-source-badge">Manual Forecast Input</span>
      </div>

      <div className="fi-field">
        <label htmlFor="fi-edit-description">Description</label>
        <input
          id="fi-edit-description"
          type="text"
          value={description}
          onChange={e => setDescription(e.target.value)}
          data-testid="fi-edit-description"
        />
      </div>
      <div className="fi-field">
        <label htmlFor="fi-edit-gl">GL Account</label>
        <input
          id="fi-edit-gl"
          type="text"
          value={glAccount}
          onChange={e => setGlAccount(e.target.value)}
          data-testid="fi-edit-gl"
        />
      </div>
      <div className="fi-field">
        <label htmlFor="fi-edit-vendor">Vendor</label>
        <input
          id="fi-edit-vendor"
          type="text"
          value={vendor}
          onChange={e => setVendor(e.target.value)}
          data-testid="fi-edit-vendor"
        />
      </div>
      <div className="fi-field">
        <label htmlFor="fi-edit-amount">Amount ($)</label>
        <input
          id="fi-edit-amount"
          type="number"
          min={0}
          value={amount}
          onChange={e => setAmount(e.target.value)}
          data-testid="fi-edit-amount"
        />
      </div>
      <div className="fi-field">
        <label htmlFor="fi-edit-owner">Owner</label>
        <input
          id="fi-edit-owner"
          type="text"
          value={owner}
          onChange={e => setOwner(e.target.value)}
          data-testid="fi-edit-owner"
        />
      </div>
      <div className="fi-field">
        <label htmlFor="fi-edit-note">Note</label>
        <input
          id="fi-edit-note"
          type="text"
          value={note}
          onChange={e => setNote(e.target.value)}
          data-testid="fi-edit-note"
        />
      </div>
      <div className="fi-field">
        <label htmlFor="fi-edit-source-ref">Source Reference</label>
        <input
          id="fi-edit-source-ref"
          type="text"
          value={sourceRef}
          onChange={e => setSourceRef(e.target.value)}
          data-testid="fi-edit-source-ref"
        />
      </div>

      <div className="fi-lineage" aria-label="Record lineage details">
        <span>Source type</span><code>manual_forecast_input</code>
        <span>Created by</span><code>{record.createdBy}</code>
        <span>Updated</span><code>{new Date(record.updatedAt).toLocaleDateString()}</code>
        <span>Input ID</span><code style={{ fontSize: 8, wordBreak: "break-all" }}>{record.inputId}</code>
      </div>

      {updateMutation.isError && !(updateMutation.error as unknown as ForecastInputOverlap)?.code && (
        <div className="source-page-error" role="alert">
          <AlertCircle size={13} aria-hidden="true" />
          <span>Update failed.</span>
        </div>
      )}

      <div className="fi-form-actions">
        <button
          className="tl-btn"
          onClick={onCancel}
          aria-label="Cancel edit"
          data-testid="fi-edit-cancel"
        >
          Cancel
        </button>
        <button
          className="tl-btn primary"
          onClick={() => handleSave(false)}
          disabled={updateMutation.isPending}
          aria-label="Save changes to this input"
          data-testid="fi-edit-save"
        >
          {updateMutation.isPending ? <Loader2 size={12} className="animate-spin" aria-hidden="true" /> : null} Save Changes
        </button>
      </div>
    </div>
  );
}

// ── ManageDrawer ──────────────────────────────────────────────────────────────

interface ManageDrawerProps {
  inputs: ForecastInputList;
  ctx: ForecastContext;
  initialTab: ManageTab;
  onAddNew: (type: InputType) => void;
  onClose: () => void;
  onRefreshed: (outlook: ForecastOutlook) => void;
}

export function ManageDrawer({
  inputs, ctx, initialTab, onAddNew, onClose, onRefreshed,
}: ManageDrawerProps) {
  const [tab, setTab] = useState<ManageTab>(initialTab);
  const [showOpen, setShowOpen] = useState(true);
  const [editingRecord, setEditingRecord] = useState<ForecastInputRecord | null>(null);

  const queryClient = useQueryClient();
  const listQueryKey = getListForecastInputsQueryKey({
    companyId: ctx.companyId,
    fiscalYear: ctx.fiscalYear,
    departmentCode: ctx.departmentCode,
    activeOnly: false,
  });
  const statusMutation = useChangeForecastInputStatus();

  const handleStatusChange = useCallback((
    inputId: string,
    status: string,
    note?: string,
    actualsRef?: string,
  ) => {
    statusMutation.mutate(
      {
        inputId,
        data: {
          actor: "ER",
          status: status as any,
          note: note ?? undefined,
          actualsLinkReference: actualsRef ?? undefined,
        },
      },
      { onSuccess: () => queryClient.invalidateQueries({ queryKey: listQueryKey }) },
    );
  }, [statusMutation, queryClient, listQueryKey]);

  const tabInputs = inputs.inputs.filter(r => r.inputType === tab);
  const displayInputs = showOpen
    ? tabInputs.filter(r => ["open", "active", "approved", "draft"].includes(r.status))
    : tabInputs;

  if (editingRecord) {
    return (
      <div className="fi-drawer" role="dialog" aria-modal="true" aria-label={`Edit ${editingRecord.description}`} data-testid="fi-manage-drawer">
        <div className="fi-drawer-head">
          <div>
            <div className="eyebrow">EDIT INPUT</div>
            <h3>{editingRecord.description}</h3>
          </div>
          <button
            onClick={() => setEditingRecord(null)}
            aria-label="Back to manage list"
            data-testid="fi-edit-back"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="fi-drawer-body">
          <EditForm
            record={editingRecord}
            onSaved={() => {
              setEditingRecord(null);
              queryClient.invalidateQueries({ queryKey: listQueryKey });
            }}
            onCancel={() => setEditingRecord(null)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fi-drawer" role="dialog" aria-modal="true" aria-label="Manage Forecast Inputs" data-testid="fi-manage-drawer">
      <div className="fi-drawer-head">
        <div>
          <div className="eyebrow">FORECAST INPUTS</div>
          <h3>Manage Inputs</h3>
        </div>
        <button
          onClick={onClose}
          aria-label="Close manage inputs drawer"
          data-testid="fi-drawer-close"
        >
          <X size={18} aria-hidden="true" />
        </button>
      </div>

      {inputs.workingForecastStale && ctx.runId && !ctx.isAuthoritative && (
        <StaleBanner
          inputs={inputs}
          runId={ctx.runId}
          onRefreshed={onRefreshed}
          isAuthoritative={ctx.isAuthoritative}
        />
      )}

      <div className="fi-drawer-tabs" role="tablist" aria-label="Forecast input types">
        {(["accrual", "commitment", "planned_spend"] as ManageTab[]).map(t => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            aria-controls={`fi-tab-panel-${t}`}
            id={`fi-tab-${t}`}
            className={tab === t ? "active" : ""}
            onClick={() => setTab(t)}
            data-testid={`fi-tab-${t}`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      <div
        className="fi-drawer-body"
        role="tabpanel"
        id={`fi-tab-panel-${tab}`}
        aria-labelledby={`fi-tab-${tab}`}
      >
        <div className="fi-list-controls">
          {tab === "accrual" && (
            <label className="fi-toggle-label" htmlFor="fi-show-open-only">
              <input
                id="fi-show-open-only"
                type="checkbox"
                checked={showOpen}
                onChange={e => setShowOpen(e.target.checked)}
                data-testid="fi-show-open-only"
              />
              Open only
            </label>
          )}
          <button
            className="tl-btn primary"
            style={{ marginLeft: "auto", padding: "7px 10px", fontSize: 10 }}
            onClick={() => onAddNew(tab)}
            disabled={ctx.isAuthoritative}
            aria-label={`Add new ${TAB_LABELS[tab].replace("s", "")}`}
            data-testid={`fi-add-new-${tab}`}
          >
            <Plus size={11} aria-hidden="true" /> Add {TAB_LABELS[tab].replace("s", "")}
          </button>
        </div>

        {displayInputs.length === 0 ? (
          <div className="fi-empty-state" data-testid={`fi-empty-${tab}`}>
            {showOpen && tabInputs.length > 0
              ? "No open records. Toggle to show all."
              : `No ${TAB_LABELS[tab].toLowerCase()} for this scope.`}
          </div>
        ) : (
          <div className="table-wrap" style={{ margin: 0 }}>
            <table className="tl-fy-table" style={{ minWidth: 560 }} aria-label={`${TAB_LABELS[tab]} list`}>
              <thead>
                <tr>
                  <th scope="col">Description</th>
                  <th scope="col">Period(s)</th>
                  <th scope="col">Amount</th>
                  <th scope="col">GL Account</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayInputs.map(record => (
                  <InputRow
                    key={record.inputId}
                    record={record}
                    onStatusChange={handleStatusChange}
                    onEdit={setEditingRecord}
                    isAuthoritative={ctx.isAuthoritative}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
